import { Button } from '@affine/component/ui/button';
import type { ConfirmModalProps } from '@affine/component/ui/modal';
import { ConfirmModal, Modal } from '@affine/component/ui/modal';
import { useI18n } from '@affine/i18n';
import { DialogTrigger } from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import * as styles from './styles.css';

export interface CancellationDeclarationForm {
  terminationReason?: string | null;
  terminationType: 'ordinary' | 'extraordinary';
}

/**
 *
 * @param param0
 * @returns
 */
export const ConfirmLoadingModal = ({
  type,
  loading,
  open,
  content,
  onOpenChange,
  onConfirm,
  ...props
}: {
  type: 'resume' | 'change';
  loading?: boolean;
  content?: ReactNode;
} & ConfirmModalProps) => {
  const t = useI18n();
  const confirmed = useRef(false);

  const title = t[`com.streamlain.payment.modal.${type}.title`]();
  const confirmText = t[`com.streamlain.payment.modal.${type}.confirm`]();
  const cancelText = t[`com.streamlain.payment.modal.${type}.cancel`]();
  const contentText =
    type !== 'change'
      ? t[`com.streamlain.payment.modal.${type}.content`]()
      : '';

  useEffect(() => {
    if (!loading && open && confirmed.current) {
      onOpenChange?.(false);
      confirmed.current = false;
    }
  }, [loading, open, onOpenChange]);

  return (
    <ConfirmModal
      title={title}
      cancelText={cancelText}
      confirmText={confirmText}
      confirmButtonOptions={{
        variant: 'primary',
        loading,
      }}
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={() => {
        confirmed.current = true;
        onConfirm?.()?.catch(console.error);
      }}
      {...props}
    >
      {content ?? contentText}
    </ConfirmModal>
  );
};

/**
 * Downgrade modal, confirm & cancel button are reversed.
 * @param variant 'personal' shows caption + personal confirm text,
 *                'team' omits caption + uses team confirm text.
 */
export const DowngradeModal = ({
  open,
  loading,
  onOpenChange,
  onCancel,
  variant = 'personal',
  planName,
  confirmationEmail,
}: {
  loading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCancel?: (declaration: CancellationDeclarationForm) => void;
  variant?: 'personal' | 'team';
  planName?: string;
  confirmationEmail?: string | null;
}) => {
  const t = useI18n();
  const canceled = useRef(false);
  const [terminationType, setTerminationType] = useState<
    'ordinary' | 'extraordinary'
  >('ordinary');
  const [terminationReason, setTerminationReason] = useState('');

  useEffect(() => {
    if (!loading && open && canceled.current) {
      onOpenChange?.(false);
      canceled.current = false;
    }
  }, [loading, open, onOpenChange]);

  // sl-ufq6: Use dynamic plan name if provided, otherwise fall back to i18n keys
  const confirmText = planName
    ? `Keep ${planName} plan`
    : variant === 'team'
      ? t['com.streamlain.payment.modal.downgrade.team-confirm']()
      : t['com.streamlain.payment.modal.downgrade.confirm']();
  const contractName = planName ? `${planName} plan` : 'Team plan';
  const contact = confirmationEmail || 'account email';

  return (
    <Modal
      title={t['com.streamlain.payment.cancellation.title']()}
      open={open}
      contentOptions={{}}
      width={480}
      onOpenChange={onOpenChange}
    >
      <div className={styles.downgradeContentWrapper}>
        <p className={styles.downgradeContent}>
          {t['com.streamlain.payment.cancellation.description']()}
        </p>
        <div className={styles.cancellationForm}>
          <label className={styles.cancellationLabel} htmlFor="cancel-kind">
            {t['com.streamlain.payment.cancellation.kind']()}
          </label>
          <select
            id="cancel-kind"
            className={styles.cancellationSelect}
            value={terminationType}
            onChange={event =>
              setTerminationType(
                event.currentTarget.value === 'extraordinary'
                  ? 'extraordinary'
                  : 'ordinary'
              )
            }
          >
            <option value="ordinary">
              {t['com.streamlain.payment.cancellation.kind-ordinary']()}
            </option>
            <option value="extraordinary">
              {t['com.streamlain.payment.cancellation.kind-extraordinary']()}
            </option>
          </select>

          {terminationType === 'extraordinary' && (
            <>
              <label
                className={styles.cancellationLabel}
                htmlFor="cancel-reason"
              >
                {t['com.streamlain.payment.cancellation.reason']()}
              </label>
              <textarea
                id="cancel-reason"
                className={styles.cancellationTextarea}
                value={terminationReason}
                onChange={event => setTerminationReason(event.target.value)}
              />
            </>
          )}

          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.identity']()}
          </div>
          <div className={styles.cancellationValue}>{contact}</div>

          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.contract']()}
          </div>
          <div className={styles.cancellationValue}>{contractName}</div>

          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.end-date']()}
          </div>
          <div className={styles.cancellationValue}>
            {t['com.streamlain.payment.cancellation.end-date-earliest']()}
          </div>

          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.confirmation-contact']()}
          </div>
          <div className={styles.cancellationValue}>{contact}</div>
        </div>
      </div>

      <footer className={styles.downgradeFooter}>
        <Button
          data-testid="subscription-cancel-confirm-button"
          onClick={() => {
            canceled.current = true;
            onCancel?.({
              terminationReason:
                terminationType === 'extraordinary'
                  ? terminationReason.trim() || null
                  : null,
              terminationType,
            });
          }}
          loading={loading}
        >
          {t['com.streamlain.payment.cancellation.confirm']()}
        </Button>
        <DialogTrigger asChild>
          <Button
            disabled={loading}
            onClick={() => onOpenChange?.(false)}
            variant="primary"
          >
            {confirmText}
          </Button>
        </DialogTrigger>
      </footer>
    </Modal>
  );
};

export const WithdrawalModal = ({
  contractLabel,
  confirmationEmail,
  loading,
  onConfirm,
  onOpenChange,
  open,
  planName,
}: {
  contractLabel?: string;
  confirmationEmail?: string | null;
  loading?: boolean;
  onConfirm?: () => Promise<void> | void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  planName?: string;
}) => {
  const t = useI18n();
  const contact = confirmationEmail || 'account email';
  const contractName =
    contractLabel ?? (planName ? `${planName} plan` : 'Subscription');

  return (
    <ConfirmModal
      title={t['com.streamlain.payment.withdrawal.title']()}
      cancelText={t['com.streamlain.payment.confirm.cancel']()}
      confirmText={t['com.streamlain.payment.withdrawal.confirm']()}
      confirmButtonOptions={{
        variant: 'primary',
        loading,
      }}
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={() => {
        void onConfirm?.();
      }}
    >
      <div className={styles.downgradeContentWrapper}>
        <p className={styles.downgradeContent}>
          {t['com.streamlain.payment.withdrawal.description']()}
        </p>
        <p className={styles.downgradeContent}>
          {t['com.streamlain.payment.withdrawal.effect']()}
        </p>
        <p className={styles.downgradeContent}>
          {t['com.streamlain.payment.withdrawal.refund-formula']()}
        </p>
        <div className={styles.cancellationForm}>
          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.identity']()}
          </div>
          <div className={styles.cancellationValue}>{contact}</div>

          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.contract']()}
          </div>
          <div className={styles.cancellationValue}>{contractName}</div>

          <div className={styles.cancellationLabel}>
            {t['com.streamlain.payment.cancellation.confirmation-contact']()}
          </div>
          <div className={styles.cancellationValue}>{contact}</div>
        </div>
      </div>
    </ConfirmModal>
  );
};
